import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import _ from "lodash";

import GetSavedQuote from "../../../components/getQuote/GetSavedQuote";
import { Loader } from "../../../components/loaders/loaders";
import { getApi, deletePayloadApi } from "../../../apis/api";
import { DeleteIcon } from "../../../assets";
import { useMessage } from "../../../context/MessageContext";
import {
  GENERAL_ERROR_MESSAGE,
  ORDER_MANAGEMENT_MESSAGES,
} from "../../../utils/message";
import SearchInput from "../../../components/shared/SearchInput";
import { RightArrowSolid } from "../../../assets";
import { GET_QUOTATION, DELETE_QUOTATION } from "../../../apis/end-points";
import { expiredData } from "../../../utils/utils";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import { RootState } from "../../../redux/store";
import {
  setPage,
  setTotalItems,
  setTotalPages,
  setLimit,
} from "../../../redux/reducer/pagination/paginationSlice";
import { clearTraditionalQuoteData } from "../../../redux/reducer/locationSlice/locationSlice";
import { DEFAULT_PAGE_SIZE, LOGISTICS_TYPE } from "../../../utils/constants";
import { saveFilters } from "../../../redux/reducer/filterSlice/filterSlice";

const SCREEN_KEY = "savedQuotation";

// Define the columns to be used in the ViewDetailsTable
const columns = [
  { header: "S.", key: "serial", className: "count", count: true },
  // {
  //   header: "Quote Id",
  //   key: "quoteId",
  //   sortable: true,
  // },
  {
    header: "Order Type",
    key: "orderType",
    sortable: true,
  },

  {
    header: "Service Name",
    key: "serviceName",
    sortable: true,
  },
  { header: "Quote Price", key: "quotePrice", dollar: true },
  { header: "Quote Status", key: "quoteStatus", className: "expired" },
];

const LTLColumns = [
  { header: "S.", key: "serial", className: "count", count: true },
  // {
  //   header: "Quote Id",
  //   key: "quoteId",
  //   sortable: true,
  // },
  {
    header: "Order Type",
    key: "orderType",
    sortable: true,
  },
  {
    header: "Service Name",
    key: "serviceName",
    sortable: true,
  },
  {
    header: "Carrier Name (LTL)",
    key: "carrierName",
    sortable: true,
  },
  {
    header: "Agent(Final Mile)",
    key: "finalmileCarrierName",
    sortable: true,
  },
  // { header: "Carrier Code", key: "carrierCode" },
  { header: "Estimated LTL Transit Days", key: "estimatedLTLTransitDay" },
  { header: "Quote Price", key: "quotePrice", dollar: true },
];

const LTLFinalMileColumns = [
  { header: "S.", key: "serial", className: "count", count: true },
  // {
  //   header: "Quote Id",
  //   key: "quoteId",
  //   sortable: true,
  // },
  {
    header: "Order Type",
    key: "orderType",
    sortable: true,
  },
  {
    header: "Service Name",
    key: "serviceName",
    sortable: true,
  },
  {
    header: "Carrier Name (LTL)",
    key: "carrierName",
    sortable: true,
  },
  {
    header: "Agent (Final Mile)",
    key: "finalmileCarrierName",
    sortable: true,
  },
  // { header: "Carrier Code", key: "carrierCode" },
  { header: "Estimated LTL Transit Days", key: "estimatedLTLTransitDay" },
  { header: "Quote Price", key: "quotePrice", dollar: true, sortable: true },
];

const SavedQuotation: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentPage = useSelector((state: RootState) => state.pagination.currentPage);
  const limit = useSelector((state: RootState) => state.pagination.limit) ?? DEFAULT_PAGE_SIZE;
  const savedScreenLimit = useSelector((state: RootState) => state.filter.savedFilters?.[SCREEN_KEY]?.paginationLimit);
  const { setMessage } = useMessage();
  const [loading, setLoading] = useState<boolean>(false);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [totalQuotation, setTotalQuotation] = useState([]);
  const [savedQuotation, setSavedQuotation] = useState([]);
  const [keyword, setKeyword] = useState<string>("");
  const [showDeleteConfModal, setShowDeleteConfModal] =
    useState<boolean>(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string[]>([]);
  const [rotatedArrow, setRotatedArrow] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);

  const toggleArrowRotation = (id: string) => {
    setRotatedArrow((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Which agent sections are closed inside each expanded Final Mile group, keyed by
  // "<quoteGroupById>:<agentHubId>". Tracking the collapsed ones rather than the open
  // ones keeps every section expanded by default, so opening a group shows all of its
  // agents and their services at once — matching Get Quote.
  const [collapsedHub, setCollapsedHub] = useState<{ [key: string]: boolean }>({});

  const toggleHub = (key: string) => {
    setCollapsedHub((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  /**
   * Section key for a saved quote row.
   *
   * A normal Final Mile hub is identified by its agentHubId alone. Dickerson quotes are
   * the exception: every one of them re-homes onto the single local "Dickerson Agent"
   * hub, so several distinct source hubs share one agentHubId and would collapse into a
   * single section. Get Quote keeps them apart because the backend returns one entry per
   * source hub, each carrying that hub's own address and distance; here the address is
   * the only thing that survives, so it is what separates them.
   *
   * Appending the address is a no-op for every other hub: it is resolved once per hub at
   * save time, so all of a hub's services carry the same value.
   */
  const hubKeyOf = (row: any) => {
    const address = row?.hubAddress;
    return [
      row?.agentHubId ?? "",
      address?.address ?? "",
      address?.city ?? "",
      address?.state ?? "",
      address?.zipCode ?? "",
    ].join("|");
  };

  /**
   * Splits a group's saved quotes into one section per agent hub, closest hub first.
   * Final Mile only — every other order type keeps the flat table.
   */
  const groupQuotesByHub = (quoteDetails: any[] = []) => {
    const order: string[] = [];
    const byHub: Record<string, any[]> = {};
    quoteDetails.forEach((row) => {
      const hubId = hubKeyOf(row);
      if (!byHub[hubId]) {
        byHub[hubId] = [];
        order.push(hubId);
      }
      byHub[hubId].push(row);
    });
    // Get Quote lists the closest hub first, each with its services priced low to high.
    // The rows were saved in that order, but JSON_ARRAYAGG gives no ordering guarantee,
    // so a group can come back shuffled. Re-apply the order here rather than depending
    // on how MySQL aggregated it. Final Mile only — no other order type renders this
    // accordion.
    const distanceOf = (rows: any[]) => {
      const distance = Number(rows?.[0]?.distanceToDestination);
      return Number.isFinite(distance) ? distance : Infinity;
    };
    return order
      .map((hubId) => ({
        hubId,
        rows: [...byHub[hubId]].sort(
          (a, b) => Number(a?.quotePrice ?? 0) - Number(b?.quotePrice ?? 0),
        ),
      }))
      .sort((a, b) => distanceOf(a.rows) - distanceOf(b.rows));
  };

  /** Header line for an agent section, mirroring the Get Quote hub caption. */
  const hubCaption = (row: any) => {
    const address = row?.hubAddress;
    const hours = address?.operationHrsFrom
      ? `${address.operationHrsFrom} - ${address?.operationHrsTo || ""}`
      : "";
    const parts = [
      address?.city,
      address?.state,
      address?.zipCode,
      address?.country,
    ].filter(Boolean);
    const distance = Number(row?.distanceToDestination);
    return `Hub: ${row?.hubName || ""} C/O ${row?.agentName || ""}${parts.length ? `, ${parts.join(", ")}` : ""
      }${hours ? ` | Hrs of Operation: ${hours}` : ""}${Number.isFinite(distance) ? ` | ${distance.toFixed(2)} mi` : ""
      }`;
  };

  // Use useCallback for debouncing the API call
  const handleKeywordChange = useCallback(
    _.debounce((inputText: string) => {
      getSavedQuotation(inputText);
    }, 2000), // 500ms delay
    [] // Empty dependency array ensures the debounced function is stable
  );

  const getSavedQuotation = async (searchKeyword = keyword) => {
    try {
      setLoading(true);
      // Assuming there's an endpoint for fetching record data with pagination
      const response = await getApi(
        `${GET_QUOTATION}?keyword=${searchKeyword}`
      );
      if (response.success && response.data) {
        const mapQuotationData = (userData: any) =>
          userData.map((user: any) => ({
            id: user?.quoteGroupById,
            page: "Save Quotation",
            customerName: user?.quotes[0]?.customer,
            orderType: user?.quotes[0]?.orderType?.name,
            orderDetails: user?.quotes[0]?.orderType,
            serviceName: user?.quotes[0]?.service?.name,
            serviceDetails: user?.quotes[0]?.service,
            destinationZipCode: user?.quotes[0]?.destinationAddress?.zipCode,
            originZipCode: user?.quotes[0]?.originAddress?.zipCode,
            destinationDetails: user?.quotes[0]?.destinationAddress,
            totalWeight: user?.quotes[0]?.destinationAddress?.weight,
            totalPieces: user?.quotes[0]?.destinationAddress?.pieces,
            quoteDetails: user?.quotes?.map((item: any, index: number) => ({
              serial: index + 1,
              quoteId: item?.id,
              quoteGroupById: user?.quoteGroupById,
              orderType: item?.orderType?.name,
              serviceName: item?.service?.name,
              description: item?.service?.description || "",
              quotePrice: Number(item?.rateDetails?.total),
              quoteStatus:
                item?.isExpiry === 1 || !expiredData(item?.createdAt)
                  ? "expired"
                  : "",
              priceDetails: item?.rateDetails,
              agentName: item?.agent?.name,
              fuelSurcharge: item?.orderType?.name === "Final Mile" ? item?.rateDetails?.customerFuelSurcharge : item?.rateDetails?.fuelSurcharge,
              additionalOtherCharges: item?.rateDetails?.additionalOtherCharges,
              basePrice: item?.rateDetails?.basePrice,
              carrierName: item?.rateDetails?.carrierName,
              carrierCode: item?.rateDetails?.carrierCode,
              estimatedLTLTransitDay: item?.rateDetails?.estimatedLTLTransitDay,
              finalmileCarrierName: item?.rateDetails?.finalmileCarrierName || "--",
              // Hub details drive the Final Mile accordion header below. A hub behind a
              // master hub reports the master's name, matching Get Quote.
              agentHubId: item?.agentHub?.id ?? "",
              hubName: item?.masterHub?.id
                ? item?.masterHub?.name
                : item?.agentHub?.name,
              // responseMidStopDetail is the address the quote was actually built
              // against — the master hub's for a hub behind one, and the Dickerson
              // source hub's for a resold Dickerson quote, which is the only place
              // that address survives. It is exactly what Get Quote captions with, so
              // prefer it and fall back for older rows that predate the column.
              hubAddress:
                item?.responseMidStopDetail ?? item?.agentHub?.address,
              // Dickerson quotes all re-home onto the one local Dickerson Agent hub, so
              // agentHubId cannot separate them; the address is what differs per source
              // hub. See groupQuotesByHub.
              isDickerson: !!item?.dickersonQuoteId,
              distanceToDestination: item?.distanceToDestination,
            })),
          }));
        const qData = mapQuotationData(response.data);
        // const indexOfLastItem = currentPage * limit;
        // const indexOfFirstItem = indexOfLastItem - limit;

        // // Slice the data based on the current page
        // const currentData = qData.slice(indexOfFirstItem, indexOfLastItem);
        setTotalQuotation(qData);
        // setSavedQuotation(currentData);
        /****************** Set Pagination Start ******************************/
        const paginationData = {
          totalItems: qData.length || 0,
          totalPages: Math.ceil(qData.length / limit) || 1,
          currentPage: currentPage || 1,
        };

        // Ensure current page doesn't exceed total pages
        if (paginationData.currentPage > paginationData.totalPages && paginationData.totalPages > 0) {
          paginationData.currentPage = paginationData.totalPages;
          dispatch(setPage(paginationData.currentPage));
        }

        dispatch(setTotalItems(paginationData.totalItems));
        dispatch(setTotalPages(paginationData.totalPages));
        // dispatch(setLimit(limit));
        /****************** Set Pagination End ******************************/
      } else {
        setMessage(response?.error?.message ?? GENERAL_ERROR_MESSAGE, "error");
      }
    } catch (error) {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    dispatch(setLimit(savedScreenLimit ?? DEFAULT_PAGE_SIZE));
    dispatch(setPage(1));
  }, [dispatch]);

  useEffect(() => {
    dispatch(saveFilters({ screenKey: SCREEN_KEY, filters: { paginationLimit: limit } as any }));
  }, [limit, dispatch]);

  useEffect(() => {
    getSavedQuotation();
  }, [currentPage]);

  // useEffect(()=>{
  //   const indexOfLastItem = currentPage * limit;
  //   const indexOfFirstItem = indexOfLastItem - limit;
  //   const currentData = totalQuotation.slice(indexOfFirstItem, indexOfLastItem);
  //   setSavedQuotation(currentData);
  //   setCheckedIds([]); setSelectAllChecked(false)
  // }, [currentPage])
  useEffect(() => {
    if (totalQuotation.length === 0) {
      setSavedQuotation([]);
      dispatch(setTotalItems(0));
      dispatch(setTotalPages(1));
      return;
    }

    const totalPages = Math.ceil(totalQuotation.length / limit);
    const validPage = Math.min(currentPage, totalPages || 1);

    const indexOfLastItem = validPage * limit;
    const indexOfFirstItem = indexOfLastItem - limit;
    const currentData = totalQuotation.slice(indexOfFirstItem, indexOfLastItem);

    dispatch(setTotalItems(totalQuotation.length));
    dispatch(setTotalPages(totalPages || 1));

    if (validPage !== currentPage) {
      dispatch(setPage(validPage));
    }

    setSavedQuotation(currentData);
    setCheckedIds([]);
    setSelectAllChecked(false);
  }, [currentPage, totalQuotation, limit, dispatch]);

  /**
   * Handles checkbox change for a row.
   * When checked, sets isModified to true, when unchecked, sets it to false.
   * @param rowIndex - Index of the row being modified.
   */
  const handleCheckboxChange = (rowId: string) => {
    if (rowId === "All") {
      setSelectAllChecked(!selectAllChecked);
      if (selectAllChecked) {
        setCheckedIds([]);
      } else {
        const ids = savedQuotation.map((item: any) => item.id);
        setCheckedIds(ids);
      }
    } else {
      const newCheckedIds = checkedIds.includes(rowId)
        ? checkedIds.filter((item: string) => item !== rowId) // Remove item
        : [...checkedIds, rowId];

      setCheckedIds && setCheckedIds(newCheckedIds);
      if (savedQuotation.length === newCheckedIds.length && !selectAllChecked) {
        setSelectAllChecked(true);
      } else if (
        savedQuotation.length !== [...checkedIds, rowId].length &&
        selectAllChecked
      ) {
        setSelectAllChecked(false);
      }
    }
  };

  const deleteQuotation = async () => {
    try {
      const apiPayload = {
        quoteGroupByIdArr: selectedQuoteId,
      };
      setLoading(true);
      const apiResponse = await deletePayloadApi(DELETE_QUOTATION, {
        data: apiPayload,
      });
      if (apiResponse.success) {
        // Reset pagination to first page before refreshing data
        dispatch(setPage(1));
        // Clear all selections
        setCheckedIds([]);
        setSelectAllChecked(false);
        // Refresh the quotation data
        await getSavedQuotation();
        setMessage(ORDER_MANAGEMENT_MESSAGES.QUOTE_DELETE_SUCCESS, "success");
        setShowDeleteConfModal(false);
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputText = e.target.value;
    setKeyword(inputText); // Update local state
    handleKeywordChange(inputText); // Call the debounced function
  };

  const handleSelect = (quoteData: any) => {
    dispatch(clearTraditionalQuoteData());
    navigate("/customer-order", {
      state: {
        page: "Save Quotation",
        id: quoteData?.id,
        isQuoteSelected: true,
        quoteId: quoteData?.quoteId,
        orderType: quoteData?.rowItem?.orderType
      },
    });
  };

  const showPopupOnDelete = (quoteId: any) => {
    setSelectedQuoteId(quoteId);
    setShowDeleteConfModal(true);
  };
  return (
    <>
      <div className="form form--innersec">
        <section className="form__section">
          <div className="form__section__title form__section__title--flex">
            <div className="formCol" style={{ width: "30%" }}>
              <SearchInput
                type="text"
                value={keyword}
                placeholder="Search by Quote Id, Zip Code"
                onChange={handleChange}
              />
            </div>
            {savedQuotation?.length ? (
              <div className="btn__group">
                <button
                  className="btn btn__danger btn__outlined btn--medium"
                  disabled={checkedIds?.length === 0}
                  onClick={() => showPopupOnDelete(checkedIds)}
                >
                  <DeleteIcon />
                  Delete All
                </button>
              </div>
            ) : (
              ""
            )}
          </div>
          {savedQuotation?.length ? (
            <div className="form__checkbox form__checkbox--gray">
              <div
                className="form__checkbox__option mb_0 mr_0"
                style={{ padding: "0px" }}
              >
                <input
                  type="checkbox"
                  id="poison"
                  className={`form__checkbox__input`}
                  checked={selectAllChecked}
                  onChange={() => handleCheckboxChange("All")}
                />
                <label className="form__checkbox__label pl_18" htmlFor="poison">
                  &nbsp; Select All
                </label>
              </div>
            </div>
          ) : (
            <div className="form__section__title form__section__title--center">
              No Results Found!
            </div>
          )}

          {savedQuotation.map((item: any, index) => (
            <div className="formRow formRow--gapNone" key={index}>
              <div className="form__section__title form__section__title--flex">
                <div className="form__section__title form__section__title--flex form__section__title--flexStart mb_0">
                  <div className="form__checkbox form__checkbox--gray">
                    <div
                      className="form__checkbox__option mb_0 mr_0"
                      style={{ padding: "0px" }}
                    >
                      <input
                        type="checkbox"
                        id="poison"
                        className={`form__checkbox__input`}
                        checked={checkedIds.includes(item?.id)}
                        onChange={() => handleCheckboxChange(item?.id)}
                      />
                      <label
                        className="form__checkbox__label pl_18"
                        htmlFor="poison"
                      >
                        &nbsp;
                      </label>
                    </div>
                  </div>
                  <span
                    style={{
                      display: "inline-block",
                      transform: rotatedArrow[item?.id]
                        ? "rotate(90deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.1s ease",
                    }}
                    onClick={() => {
                      toggleArrowRotation(item?.id);
                    }}
                  >
                    <RightArrowSolid />
                  </span>

                  <button
                    //href="javascript:void(0)"
                    className="form__section__title form__section__title--second mb_0"
                    onClick={() => {
                      // Clicking the Quote ID line stays on the Saved Quotes page
                      // and toggles the inline quote-details expansion (same as the
                      // arrow) — it must NOT redirect to the Customer Order page.
                      // goToViewQuote(item); // disabled: redirected to Customer Order
                      toggleArrowRotation(item?.id);
                    }}
                  >
                    {item?.orderType === "Final Mile"
                      ? `Quote Id: ${item.id} | Customer Name: ${item?.customerName?.name}

                  | ${item?.serviceDetails?.logisticsType === LOGISTICS_TYPE.PICKUP ? "Origin" : "Destination"} Zip Code: ${item?.destinationZipCode} | Total Weight:
                  ${item?.totalWeight} | Total Pieces: ${item?.totalPieces}`
                      : item?.orderType === "LTL"
                        ? `Quote Id: ${item.id} | Customer Name: ${item?.customerName?.name} |
                  Origin Zip Code: ${item?.originZipCode} | Destination Zip Code: ${item?.destinationZipCode} | Total Weight:
                  ${item?.totalWeight} | Total Pieces: ${item?.totalPieces}`
                        : `Quote Id: ${item.id} | Customer Name: ${item?.customerName?.name}
                  | Origin Zip Code: ${item?.originZipCode} | Destination Zip Code: ${item?.destinationZipCode} | Total Weight:
                  ${item?.totalWeight} | Total Pieces: ${item?.totalPieces}`}
                  </button>
                </div>
                <div className="btn__group mb_0">
                  <button
                    className="btn btn__danger btn__outlined btn--medium"
                    onClick={() => showPopupOnDelete([item?.id])}
                  >
                    Delete Quote
                  </button>
                </div>
              </div>
              {rotatedArrow[item?.id] === true &&
                (item?.orderType === "Final Mile" ? (
                  // A saved Final Mile quote can span several agents, so it reads as
                  // one collapsible section per agent — the same shape as Get Quote.
                  groupQuotesByHub(item?.quoteDetails).map(
                    (hub: any) => {
                      const hubKey = `${item?.id}:${hub.hubId}`;
                      const isHubOpen = !collapsedHub[hubKey];
                      return (
                        // One indent for the whole section, sized to the group row's
                        // checkbox so this arrow lines up under that row's arrow and the
                        // table's left edge lines up with the arrow — as in the design.
                        <div key={hubKey} style={{ paddingLeft: "1.875rem" }}>
                          {/* Mirrors the quote-group row above: --flex is space-between,
                              so the arrow and caption share a --flexStart wrapper to stay
                              together on the left. */}
                          <div className="form__section__title form__section__title--flex">
                            <div className="form__section__title form__section__title--flex form__section__title--flexStart mb_0">
                              <span
                                style={{
                                  display: "inline-block",
                                  cursor: "pointer",
                                  transform: isHubOpen
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                                  transition: "transform 0.1s ease",
                                }}
                                onClick={() => toggleHub(hubKey)}
                              >
                                <RightArrowSolid />
                              </span>
                              <button
                                className="form__section__title form__section__title--second mb_0"
                                onClick={() => toggleHub(hubKey)}
                              >
                                {hubCaption(hub.rows[0])}
                              </button>
                            </div>
                          </div>
                          {isHubOpen && (
                            <div className="tableWrap tableWrap--order tableWrap--nested pt_0 pl_0">
                              <div className="tableContainer">
                                <GetSavedQuote
                                  columns={columns}
                                  data={hub.rows}
                                  orderType={item?.orderType}
                                  handleSelect={handleSelect}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }
                  )
                ) : (
                  <div className="tableWrap tableWrap--order pt_0">
                    <div className="tableContainer">
                      <GetSavedQuote
                        columns={
                          item?.orderType === "LTL"
                            ? LTLColumns
                            : item?.orderType === "LTL + Final Mile"
                              ? LTLFinalMileColumns
                              : columns
                        }
                        data={item?.quoteDetails}
                        orderType={item?.orderType}
                        handleSelect={handleSelect}
                      />
                    </div>
                  </div>
                ))}
            </div>
          ))}
        </section>
      </div>
      {showDeleteConfModal && (
        <ConfirmModal
          confirmInfo={{
            heading: "Delete Quote!",
            para: "Do you want to delete the selected saved quote",
          }}
          onConfirm={deleteQuotation}
          setConfirmModal={() => setShowDeleteConfModal(false)}
          firstMessageShow={true}
        />
      )}
    </>
  );
};

export default SavedQuotation;
